"use client";

import {useEffect, useMemo, useState} from "react";
import clsx from "clsx";
import {diffArrays, diffWordsWithSpace} from "diff";
import {useTranslation} from "react-i18next";
import Icon from "@/components/library/Icon";
import {snapshotValuesEqual, type MutationProposal} from "@/services/wingman/mutation-proposal";
import type {UseCase} from "@/types/scripts";
import {fetchUseCasesBatch} from "@/utils/scripts/liveReflectionData";
import {TextDiff} from "./ProposalDiff";
import ProposalFieldLabel from "./ProposalFieldLabel";
import {occurrenceKeys, valueKey} from "./snapshotKeys";

type Snapshot = Record<string, unknown>;
type Titles = Map<string, string>;
type ItemChange =
   | {kind: "added" | "removed" | "unchanged"; item: unknown; moved?: {from: number; to: number}}
   | {kind: "edited"; before: Snapshot; after: Snapshot; moved?: {from: number; to: number}};

const isRecord = (value: unknown): value is Snapshot => !!value && typeof value === "object" && !Array.isArray(value);
/** No content to show. `undefined` (field absent) and `null` (explicitly cleared) still render differently. */
const isBlank = (value: unknown) => value == null || value === "" || (Array.isArray(value) && !value.length);
/** Wiring the reviewer cannot act on. Position is reported as a move instead. */
const INTERNAL_KEYS = new Set(["id", "order", "status", "textScriptId", "type"]);
const TITLE_KEYS = ["title", "name", "question", "label"] as const;

const itemId = (item: unknown) => isRecord(item) && typeof item.id === "string" ? item.id : undefined;

function ownTitle(item: Snapshot): string | undefined {
   for (const key of TITLE_KEYS) {
      const field = item[key];
      if (typeof field === "string" && field) return field;
   }
   return undefined;
}

function itemTitle(item: unknown, titles: Titles): string | undefined {
   if (!isRecord(item)) return;
   const title = ownTitle(item);
   if (title) return title;
   const id = itemId(item);
   return id ? titles.get(id) : undefined;
}

function rememberCollectionTitles(titles: Titles, value: unknown) {
   if (!Array.isArray(value)) return;
   for (const item of value) {
      const id = itemId(item);
      const title = isRecord(item) ? ownTitle(item) : undefined;
      if (id && title && !titles.has(id)) titles.set(id, title);
   }
}

/** Associations carry only IDs; their titles live in sibling collections of either snapshot. */
function collectTitles(...snapshots: Snapshot[]): Titles {
   const titles: Titles = new Map();
   for (const snapshot of snapshots) for (const value of Object.values(snapshot)) rememberCollectionTitles(titles, value);
   return titles;
}

/** Associations reference actions by ID only; load the untitled ones so reviewers see what is being linked. */
function useLinkedUseCases(snapshots: Snapshot[], titles: Titles) {
   const key = [...new Set(snapshots.flatMap(snapshot => Array.isArray(snapshot.useCaseAssociations) ? snapshot.useCaseAssociations : [])
      .map(itemId).filter((id): id is string => !!id && !titles.has(id)))].sort((left, right) => left.localeCompare(right, "en")).join(",");
   const [useCases, setUseCases] = useState<Map<string, UseCase>>(() => new Map());
   useEffect(() => {
      if (!key) return;
      let live = true;
      fetchUseCasesBatch(key.split(","), true).then(found => { if (live) setUseCases(found); }).catch(() => {});
      return () => { live = false; };
   }, [key]);
   return useCases;
}

function withLinkedUseCases(snapshot: Snapshot, useCases: Map<string, UseCase>): Snapshot {
   if (!useCases.size || !Array.isArray(snapshot.useCaseAssociations)) return snapshot;
   return {...snapshot, useCaseAssociations: snapshot.useCaseAssociations.map(link => {
      const useCase = useCases.get(itemId(link) ?? "");
      return useCase && isRecord(link) ? {title: useCase.title, objective: useCase.description,
         steps: useCase.actions.map(action => action.description).filter(Boolean), ...link} : link;
   })};
}

const withoutInternal = (item: Snapshot) => Object.fromEntries(Object.entries(item).filter(([key]) => !INTERNAL_KEYS.has(key)));

/** Content-matched items read as one edit only while most of their words survive; otherwise as a swap. */
function readsAsSameItem(before: Snapshot, after: Snapshot) {
   const text = (item: Snapshot) => Object.entries(withoutInternal(item)).map(([, value]) => typeof value === "string" ? value : JSON.stringify(value)).join("\n");
   const parts = diffWordsWithSpace(text(before), text(after));
   const kept = parts.filter(part => !part.added && !part.removed).reduce((sum, part) => sum + part.value.trim().length, 0);
   const total = Math.max(text(before).trim().length, text(after).trim().length);
   return total === 0 || kept / total >= 0.5;
}

function addedValues(run: {added?: boolean; value: unknown[]}, next: {added?: boolean; value: unknown[]} | undefined): unknown[] {
   if (run.added) return run.value;
   if (next?.added) return next.value;
   return [];
}

/** Items with IDs are matched by ID so edits and moves read as such; others by content. */
export function diffItems(before: unknown[], after: unknown[]): ItemChange[] {
   const keyed = [...before, ...after].every(item => itemId(item));
   if (!keyed) {
      const changes: ItemChange[] = [];
      const runs = diffArrays(before, after, {comparator: snapshotValuesEqual});
      runs.forEach((run, index) => {
         if (!run.added && !run.removed) return run.value.forEach(item => changes.push({kind: "unchanged", item}));
         if (run.added && runs[index - 1]?.removed) return;
         const next = runs[index + 1];
         const removed = run.removed ? run.value : [];
         const added = addedValues(run, next);
         removed.forEach((item, i) => {
            const replacement = added[i];
            if (isRecord(item) && isRecord(replacement) && readsAsSameItem(item, replacement)) changes.push({kind: "edited", before: item, after: replacement});
            else changes.push({kind: "removed", item}, ...(i < added.length ? [{kind: "added" as const, item: replacement}] : []));
         });
         added.slice(removed.length).forEach(item => changes.push({kind: "added", item}));
      });
      return changes;
   }
   const beforeIds = before.map(item => itemId(item)!);
   const afterIds = after.map(item => itemId(item)!);
   const shared = afterIds.filter(id => beforeIds.includes(id));
   const stayed = new Set(diffArrays(beforeIds.filter(id => afterIds.includes(id)), shared)
      .filter(run => !run.added && !run.removed).flatMap(run => run.value));
   const changes: ItemChange[] = after.map((item, index) => {
      const id = afterIds[index];
      const from = beforeIds.indexOf(id);
      if (from < 0) return {kind: "added", item};
      const moved = stayed.has(id) ? undefined : {from: from + 1, to: index + 1};
      const previous = before[from];
      return snapshotValuesEqual(withoutInternal(previous as Snapshot), withoutInternal(item as Snapshot)) &&
         snapshotValuesEqual((previous as Snapshot).status, (item as Snapshot).status)
         ? {kind: "unchanged", item, moved}
         : {kind: "edited", before: previous as Snapshot, after: item as Snapshot, moved};
   });
   before.forEach((item, index) => {
      if (!afterIds.includes(beforeIds[index])) changes.splice(Math.min(index, changes.length), 0, {kind: "removed", item});
   });
   return changes;
}

function changeKey(change: ItemChange): string {
   if (change.kind === "edited") return `edited:${valueKey(change.before)}=>${valueKey(change.after)}`;
   return `${change.kind}:${valueKey(change.item)}`;
}

function primitiveText(value: unknown): string | undefined {
   if (typeof value === "string") return value;
   if (typeof value === "number") return String(value);
   if (typeof value === "bigint") return value.toString();
   return undefined;
}

function SnapshotValue({value, titles}: {value: unknown; titles: Titles}) {
   const {t} = useTranslation("ask");
   if (value === undefined) return <span className="italic text-500">{t("toolApproval.absentValue")}</span>;
   if (isBlank(value)) return <span className="italic text-500">{t("toolApproval.emptyValue")}</span>;
   if (typeof value === "boolean") return <>{t(value ? "toolApproval.trueValue" : "toolApproval.falseValue")}</>;
   if (Array.isArray(value)) return <ul className="space-y-1">{occurrenceKeys(value, valueKey).map(({key, item}) => <li key={key}><SnapshotValue value={item} titles={titles} /></li>)}</ul>;
   if (isRecord(value)) return <ItemBody item={value} titles={titles} />;
   const text = primitiveText(value);
   return text === undefined ? null : <span className="whitespace-pre-wrap [overflow-wrap:anywhere]">{text}</span>;
}

function ItemBody({item, titles, showTitle = true}: {item: unknown; titles: Titles; showTitle?: boolean}) {
   if (!isRecord(item)) return <SnapshotValue value={item} titles={titles} />;
   const title = itemTitle(item, titles);
   const fields = Object.entries(item).filter(([key, value]) =>
      !INTERNAL_KEYS.has(key) && !isBlank(value) && !(title && TITLE_KEYS.some(titleKey => titleKey === key && value === title)));
   return <div className="space-y-1.5">
      {title && showTitle && <p className="font-medium text-900">{title}</p>}
      {fields.map(([key, value]) => <div key={key}>
         {(fields.length > 1 || !title) && <p className="text-xs font-medium text-500"><ProposalFieldLabel field={key} /></p>}
         {key === "steps" && Array.isArray(value)
            ? <ol className="list-decimal space-y-0.5 pl-5 marker:text-500">{occurrenceKeys(value, valueKey).map(({key: stepKey, item: step}) => <li key={stepKey}><SnapshotValue value={step} titles={titles} /></li>)}</ol>
            : <SnapshotValue value={value} titles={titles} />}
      </div>)}
   </div>;
}

/** Only the parts that differ, labelled by field. */
function RecordDiff({before, after, titles}: {before: Snapshot; after: Snapshot; titles: Titles}) {
   const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])]
      .filter(key => key !== "order" && key !== "id" && !snapshotValuesEqual(before[key], after[key]));
   return <div className="space-y-2">
      {keys.map(key => <div key={key}>
         <p className="text-xs font-medium text-500"><ProposalFieldLabel field={key} /></p>
         <ValueDiff before={before[key]} after={after[key]} titles={titles} />
      </div>)}
   </div>;
}

/** Matches the draft-review badges (DraftReviewDialog, ReviewResponseDiff). */
function ItemTag({kind}: {kind: "added" | "removed" | "edited"}) {
   const {t} = useTranslation("ask");
   const tone = {
      added: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
      removed: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
      edited: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
   }[kind];
   return <span className={clsx("shrink-0 rounded px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wider", tone)}>
      {t(`toolApproval.${kind}`)}
   </span>;
}

function ListDiff({before, after, titles}: {before: unknown[]; after: unknown[]; titles: Titles}) {
   const {t} = useTranslation("ask");
   const changes = diffItems(before, after);
   const unchanged = changes.filter(change => change.kind === "unchanged" && !change.moved).length;
   return <ul className="space-y-2">
      {occurrenceKeys(changes, changeKey).map(({key, item: change}) => {
         if (change.kind === "unchanged" && !change.moved) return null;
         const title = change.kind === "edited" ? itemTitle(change.after, titles) ?? itemTitle(change.before, titles) : itemTitle(change.item, titles);
         return <li key={key} className="rounded-xl bg-panel_secondary p-3">
            <div className="flex flex-wrap items-center gap-2">
               {title && <p className={clsx("min-w-0 font-medium", change.kind === "removed" ? "text-500 line-through decoration-red-400/70" : "text-900")}>{title}</p>}
               {change.kind !== "unchanged" && <ItemTag kind={change.kind} />}
               {change.moved && <span className="text-xs text-500">{t("toolApproval.moved", change.moved)}</span>}
            </div>
            {change.kind === "edited"
               ? <div className="mt-1.5"><RecordDiff before={change.before} after={change.after} titles={titles} /></div>
               : (change.kind !== "unchanged" || !title) && <div className={clsx("mt-1.5", change.kind === "removed" && "text-500")}><ItemBody item={change.item} titles={titles} showTitle={false} /></div>}
         </li>;
      })}
      {unchanged > 0 && <li className="text-xs text-500">{t("toolApproval.unchangedItems", {count: unchanged})}</li>}
   </ul>;
}

function ValueDiff({before, after, titles}: {before: unknown; after: unknown; titles: Titles}) {
   if (typeof before === "string" && typeof after === "string" && before.trim() && after.trim())
      return <TextDiff before={before} after={after} />;
   if (Array.isArray(before ?? []) && Array.isArray(after ?? []) && (Array.isArray(before) || Array.isArray(after)))
      return <ListDiff before={(before ?? []) as unknown[]} after={(after ?? []) as unknown[]} titles={titles} />;
   if (isRecord(before) && isRecord(after)) return <RecordDiff before={before} after={after} titles={titles} />;
   // A blank before is only worth showing when after is blank too, e.g. absent -> null.
   const showBefore = !isBlank(before) || isBlank(after);
   return <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
      {showBefore && (isBlank(before)
         ? <span className="text-500"><SnapshotValue value={before} titles={titles} /></span>
         : <del className="rounded-sm bg-red-100 px-1 text-red-800 decoration-red-400 dark:bg-red-950/60 dark:text-red-200"><SnapshotValue value={before} titles={titles} /></del>)}
      {showBefore && <Icon icon="arrow-right" className="h-3 w-3 self-center text-400" />}
      {isBlank(after)
         ? <span className="text-500"><SnapshotValue value={after} titles={titles} /></span>
         : <ins className="rounded-sm bg-green-100 px-1 text-green-900 no-underline dark:bg-green-950/60 dark:text-green-200"><SnapshotValue value={after} titles={titles} /></ins>}
   </div>;
}

export function changedFields(preview: MutationProposal) {
   return [...new Set([...Object.keys(preview.before), ...Object.keys(preview.after)])].filter(key =>
      !snapshotValuesEqual(preview.before[key], preview.after[key]) || Object.hasOwn(preview.before, key) !== Object.hasOwn(preview.after, key));
}

/** Compare the frozen snapshots, including null/absent values and collection additions/removals. */
export default function ScriptSnapshotDiff({preview}: {preview: MutationProposal}) {
   const {t} = useTranslation("ask");
   const snapshotTitles = collectTitles(preview.after, preview.before);
   const useCases = useLinkedUseCases([preview.before, preview.after], snapshotTitles);
   const [before, after] = useMemo(() => [withLinkedUseCases(preview.before, useCases), withLinkedUseCases(preview.after, useCases)], [preview, useCases]);
   const changed = changedFields(preview);
   const titles = collectTitles(after, before);
   return <div>
      {occurrenceKeys(preview.warnings, warning => warning).map(({key, item: warning}) => <p key={key} role="note" className="mx-4 mt-4 flex gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
         <Icon icon="triangle-exclamation" className="mt-1 h-3 w-3 shrink-0" />{warning}
      </p>)}
      {!changed.length && <p className="px-4 py-4 text-sm text-600">{t("toolApproval.noChanges")}</p>}
      <ul className="divide-y divide-neutral">
         {changed.map(field => <li key={field} className="px-4 py-4">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-500"><ProposalFieldLabel field={field} /></h3>
            <div className="text-sm leading-6 text-800"><ValueDiff before={before[field]} after={after[field]} titles={titles} /></div>
         </li>)}
      </ul>
   </div>;
}
